import React from "react";
import { Select } from "antd";
import { ReimbursementFormData } from "./types";

interface ReimbursementFormHeaderProps {
    formData: ReimbursementFormData;
    handleInputChange: (e: { target: { name: string; value: any } }) => void;
    reimbursementTypes: any[];
    reimbursementMasters: any;
    reimbursementTypesLoading: boolean;
    mastersLoading: boolean;
    requestTypes: any[];
    requestTypesLoading: boolean;
    ltcSchemes: any[];
    ltcSchemesLoading: boolean;
    blockTypes: any[];
    blockTypesLoading: boolean;
    validationErrors: { [key: string]: string };
    getBlockTypeDateRange: (blockType: any) => string;
    readOnly?: boolean;
    associatedRefData?: any[] | null;
    associatedRefLoading?: boolean;
    isAssocRefStage?: boolean;
}

export const ReimbursementFormHeader: React.FC<ReimbursementFormHeaderProps> = ({
    formData,
    handleInputChange,
    reimbursementTypes,
    reimbursementMasters,
    reimbursementTypesLoading,
    mastersLoading,
    requestTypes,
    requestTypesLoading,
    ltcSchemes,
    ltcSchemesLoading,
    blockTypes,
    blockTypesLoading,
    validationErrors,
    getBlockTypeDateRange,
    readOnly = false,
    associatedRefData = null,
    associatedRefLoading = false,
    isAssocRefStage = false,
}) => {
    return (
        <div className="card shadow-sm border-0 mb-4 rounded-4 bg-light bg-opacity-50">
            <div className="card-body p-4">
                <div className="row g-3">
                    {/* Reimbursement Type Dropdown */}
                    <div className="col-md-4">
                        <div className="form-group mb-0">
                            <label className="form-label text-dark fw-bold mb-1">
                                Reimbursement Type <span className="text-danger">*</span>
                            </label>
                            <Select
                                showSearch
                                placeholder="Select Reimbursement Type"
                                className="w-100"
                                style={{ height: '40px', borderRadius: '8px' }}
                                value={formData.ReimbursementType || undefined}
                                onChange={(value) => {
                                    handleInputChange({ target: { name: "ReimbursementType", value } });
                                }}
                                disabled={readOnly || reimbursementTypesLoading || mastersLoading}
                                filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={
                                    ((reimbursementTypes && reimbursementTypes.length > 0)
                                        ? reimbursementTypes.map((type: any) => {
                                            const code = type.reimbursement_type_id || type.ReimbursementType || type.type_id || type.reimbursement_type || '';
                                            const name = type.reimbursement_type_name || type.Ddtext || type.type_text || type.reimbursement_type_text || '';
                                            return {
                                                value: code,
                                                label: name ? `${code} - ${name}` : code,
                                            };
                                        })
                                        : reimbursementMasters?.Reimbursement_Type?.map((type: any) => {
                                            const code = type.reimbursement_type_id || type.type_id || type.ReimbursementType || '';
                                            const name = type.reimbursement_type_name || type.type_text || type.Ddtext || '';
                                            return {
                                                value: code,
                                                label: name ? `${code} - ${name}` : code,
                                            };
                                        }) || []
                                    ).sort((a: any, b: any) =>
                                        String(a.label || "").toLowerCase().localeCompare(String(b.label || "").toLowerCase())
                                    )
                                }
                            />
                            {validationErrors.ReimbursementType && (
                                <div className="invalid-feedback ps-2">
                                    {validationErrors.ReimbursementType}
                                </div>
                            )}
                            {(reimbursementTypesLoading || mastersLoading) && (
                                <div className="mt-2 ps-2 d-flex align-items-center gap-2">
                                    <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                    <small className="text-muted fw-medium">Fetching types...</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Request Type Dropdown */}
                    {formData.ReimbursementType && (
                        <div className="col-md-4">
                            <div className="form-group mb-0">
                                <label className="form-label text-dark fw-bold mb-1">
                                    Request Type <span className="text-danger">*</span>
                                </label>
                                <Select
                                    showSearch
                                    placeholder={requestTypesLoading ? "Loading..." : "Select Request Type"}
                                    className="w-100"
                                    style={{ height: '40px', borderRadius: '8px' }}
                                    value={formData.RequestType || undefined}
                                    onChange={(value) => {
                                        handleInputChange({ target: { name: "RequestType", value } });
                                    }}
                                    loading={requestTypesLoading}
                                    disabled={readOnly || requestTypesLoading}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={
                                        (requestTypes && requestTypes.length > 0)
                                            ? requestTypes.map((rt: any) => ({
                                                value: rt.request_type_id || rt.RequestType,
                                                label: rt.request_type_text || rt.Ddtext || rt.request_type_id,
                                            }))
                                            : []
                                    }
                                    notFoundContent={
                                        requestTypesLoading
                                            ? <span className="text-muted small">Loading request types...</span>
                                            : <span className="text-muted small">No request types available</span>
                                    }
                                />
                                {requestTypesLoading && (
                                    <div className="mt-2 ps-1 d-flex align-items-center gap-2">
                                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: "#A93226", width: '14px', height: '14px' }}></div>
                                        <small className="text-muted fw-medium">Fetching request types...</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* LTC Scheme Dropdown */}
                    {formData.RequestType && (formData.ReimbursementType === "ZLTA" || (ltcSchemes && ltcSchemes.length > 0)) && (
                        <div className="col-md-4">
                            <div className="form-group mb-0">
                                <label className="form-label text-dark fw-bold mb-1">
                                    Scheme Type
                                </label>
                                <Select
                                    placeholder="Select LTC Scheme"
                                    className="w-100"
                                    style={{ height: '40px', borderRadius: '8px' }}
                                    value={formData.LTCScheme || undefined}
                                    onChange={(value) => {
                                        handleInputChange({ target: { name: "LTCScheme", value } });
                                    }}
                                    disabled={readOnly}
                                    loading={ltcSchemesLoading}
                                    options={ltcSchemes.map((scheme: any) => ({
                                        value: scheme.ltc_scheme_code,
                                        label: scheme.ltc_scheme_text
                                    }))}
                                />
                            </div>
                        </div>
                    )}

                    {/* LTC Block Type Dropdown */}
                    {formData.ReimbursementType === "ZLTA" && formData.LTCScheme && (
                        <div className="col-md-4">
                            <div className="form-group mb-0">
                                <label className="form-label text-dark fw-bold mb-1">
                                    LTC Block
                                </label>
                                <Select
                                    placeholder="Select Block Type"
                                    className="w-100"
                                    style={{ height: '40px', borderRadius: '8px' }}
                                    value={formData.BlockType || undefined}
                                    onChange={(value) => {
                                        handleInputChange({ target: { name: "BlockType", value } });
                                    }}
                                    disabled={readOnly}
                                    loading={blockTypesLoading}
                                    options={blockTypes.map((blockType: any, idx: number) => {
                                        const optValue = blockType.block_key || blockType.id || blockType.ltc_block_type || String(idx);
                                        return {
                                            value: optValue,
                                            label: getBlockTypeDateRange(blockType)
                                        };
                                    })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Block Begin Date and Block End Date Readonly Fields */}
                    {formData.ReimbursementType === "ZLTA" && formData.LTCScheme && formData.BlockType && (() => {
                        const selectedBlock = (blockTypes || []).find((b: any, idx: number) => {
                            const optValue = b.block_key || b.id || b.ltc_block_type || String(idx);
                            return optValue === formData.BlockType || b.ltc_block_type === formData.BlockType || String(b.id) === String(formData.BlockType);
                        });
                        const rawStart = selectedBlock?.StartDate || selectedBlock?.start_date || selectedBlock?.startDate;
                        const rawEnd = selectedBlock?.EndDate || selectedBlock?.end_date || selectedBlock?.endDate;

                        const parseToDateString = (val: any, fallback: string) => {
                            if (!val) return fallback;
                            if (typeof val === "string" && val.includes("/Date(")) {
                                const ms = parseInt(val.replace(/\/Date\((.*?)\)\//, "$1"), 10);
                                if (!isNaN(ms)) {
                                    const d = new Date(ms);
                                    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                                }
                            }
                            const d = new Date(val);
                            if (!isNaN(d.getTime())) {
                                return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
                            }
                            return String(val);
                        };

                        const extractYears = (bType: any): number => {
                            if (!bType) return 4;
                            if (typeof bType.years === "number") return bType.years;
                            if (typeof bType.block_years === "number") return bType.block_years;
                            const name = String(bType.ltc_block_type || bType.name || formData.BlockType || "");
                            if (typeof name === "string" && typeof name.match === "function") {
                                const match = name.match(/(\d+)\s*(?:year|yr|y)/i);
                                if (match && match[1]) {
                                    const parsed = parseInt(match[1], 10);
                                    if (!isNaN(parsed) && parsed > 0) return parsed;
                                }
                            }
                            return 4;
                        };

                        const years = extractYears(selectedBlock);
                        const fallbackEnd = new Date(2029, 11, 31);
                        const fallbackStart = new Date(fallbackEnd.getFullYear() - years + 1, 0, 1);
                        const formatDateStr = (d: Date) =>
                            `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;

                        const startDateStr = parseToDateString(rawStart, formatDateStr(fallbackStart));
                        const endDateStr = parseToDateString(rawEnd, formatDateStr(fallbackEnd));

                        return (
                            <>
                                <div className="col-md-4">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-dark fw-bold mb-1">
                                            Block Begin Date
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={startDateStr}
                                            readOnly
                                            disabled
                                            style={{ height: '40px', borderRadius: '8px', backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group mb-0">
                                        <label className="form-label text-dark fw-bold mb-1">
                                            Block End Date
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={endDateStr}
                                            readOnly
                                            disabled
                                            style={{ height: '40px', borderRadius: '8px', backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                        />
                                    </div>
                                </div>
                            </>
                        );
                    })()}

                    {/* Associated Reference Dropdown */}
                    {isAssocRefStage && (
                        <div className="col-md-4">
                            <div className="form-group mb-0">
                                <label className="form-label text-dark fw-bold mb-1">
                                    Associated Reference <span className="text-danger">*</span>
                                </label>
                                {associatedRefData !== null && associatedRefData.length === 0 ? (
                                    <div className="d-flex align-items-center bg-light px-3 rounded border" style={{ height: '40px', borderColor: '#d9d9d9' }}>
                                        <span className="text-muted small">
                                            No Open Applications for the specified reimbursement type
                                        </span>
                                    </div>
                                ) : (
                                    <Select
                                        showSearch
                                        placeholder={associatedRefLoading ? "Loading..." : "Select Reference"}
                                        className="w-100"
                                        style={{ height: '40px', borderRadius: '8px' }}
                                        value={formData.AssociatedReference || undefined}
                                        onChange={(value) => {
                                            handleInputChange({ target: { name: "AssociatedReference", value } });
                                        }}
                                        loading={associatedRefLoading}
                                        disabled={readOnly || associatedRefLoading}
                                        filterOption={(input, option) =>
                                            String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={
                                            (associatedRefData ?? []).map((item: any) => ({
                                                value: item.ReferenceNumber,
                                                label: item.Text,
                                            }))
                                        }
                                    />
                                )}
                                {validationErrors.AssociatedReference && (
                                    <div className="invalid-feedback ps-2 d-block">
                                        {validationErrors.AssociatedReference}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
